import { FC, ReactNode, useCallback, useState } from 'react';

import { styled } from '../theme';
import { CommonComponentProps } from '../types/components';
import { FlexRow, SvgIcon } from './';

const Container = styled('section')(({ theme }) => ({
    background: theme.color.white,
    display: 'flex',
    flexFlow: 'column',
    width: '100%',
    borderRadius: theme.borderRadius.base,
    padding: '0 15px 15px 15px',

    [theme.breakpoint.md]: {
        padding: '0 30px 50px 30px',
    },
}));

const ContentWrapper = styled('div')<{ isCollapsed: boolean; hasTitle?: boolean }>(
    ({ theme, isCollapsed, hasTitle }) => ({
        background: theme.color.white,
        display: 'flex',
        flexFlow: 'column',
        width: '100%',

        ...(hasTitle
            ? { display: isCollapsed ? 'none' : 'flex' }
            : {
                  height: isCollapsed ? 90 : 'auto',
                  overflowY: isCollapsed ? 'hidden' : 'visible',
              }),
    })
);

const CollapseBtn = styled(FlexRow)(({ theme }) => ({
    cursor: 'pointer',
    height: 60,
    width: 'calc(100% + 30px)',
    marginBottom: -15,
    marginLeft: -15,

    color: theme.color.gray40,
    ':hover': {
        color: theme.color.green1,
    },

    [theme.breakpoint.md]: {
        height: 80,
        width: 'calc(100% + 60px)',
        marginBottom: -50,
        marginLeft: -30,
    },
}));

const Header = styled(FlexRow)<{ isCollapsed: boolean }>(({ isCollapsed }) => ({
    cursor: 'pointer',
    marginBottom: isCollapsed ? 0 : 25,
}));

type SectionBodyProps = {
    isCollapsible?: boolean;
    isCollapsedInitially?: boolean;
    title?: ReactNode;
} & CommonComponentProps;

const SectionBody: FC<SectionBodyProps> = ({
    children,
    isCollapsible,
    isCollapsedInitially,
    title,
    ...props
}: SectionBodyProps) => {
    const [isCollapsed, setCollapsed] = useState(!!(isCollapsible && isCollapsedInitially));

    const toggleCollapse = useCallback(() => {
        setCollapsed((prev) => !prev);
    }, []);

    const iconSrc = isCollapsed ? '/icons/ic-arrow-down.svg' : '/icons/ic-arrow-up.svg';

    return (
        <Container {...props}>
            {!!title && !!isCollapsible && (
                <Header
                    onClick={toggleCollapse}
                    justify="space-between"
                    align="center"
                    isCollapsed={isCollapsed}
                >
                    {title}
                    <SvgIcon src={iconSrc} size="lg" />
                </Header>
            )}
            {!(!!title && isCollapsed) && (
                <ContentWrapper isCollapsed={isCollapsed} hasTitle={!!title}>
                    {children}
                </ContentWrapper>
            )}
            {!!isCollapsible && !title && (
                <CollapseBtn onClick={toggleCollapse} justify="center" align="center">
                    <SvgIcon src={iconSrc} size="lg" />
                </CollapseBtn>
            )}
        </Container>
    );
};

export default SectionBody;
